import type { AnyExtension } from "@tiptap/core";
import Highlight from "@tiptap/extension-highlight";
import Strike from "@tiptap/extension-strike";
import { ClassTextAlign } from "./ClassTextAlign";

// Beats ProseMirror's default parse priority (50) so plain `<mark>` reaches PlainHighlight before ClassMark.
const PLAIN_HIGHLIGHT_PARSE_PRIORITY = 55;

/**
 * Highlight limited to plain `<mark>`; classed marks fall through to ClassMark.
 */
export const PlainHighlight = Highlight.extend({
  parseHTML() {
    return [
      {
        tag: "mark",
        priority: PLAIN_HIGHLIGHT_PARSE_PRIORITY,
        getAttrs: (node) => {
          if (node instanceof HTMLElement && node.hasAttribute("class")) {
            return false;
          }
          return null;
        },
      },
    ];
  },
});

export const RICH_FORMATTING_EXTENSIONS = [
  "strike",
  "highlight",
  "textAlign",
] as const;

export type RichFormattingExtension =
  (typeof RICH_FORMATTING_EXTENSIONS)[number];

interface RichFormattingEntry {
  extension: AnyExtension;
  /** Surface class that scopes the extension's styles in TextEditor.scss. */
  surfaceClassName: string;
}

const richFormattingRegistry: Record<
  RichFormattingExtension,
  RichFormattingEntry
> = {
  strike: { extension: Strike, surfaceClassName: "has-strike" },
  highlight: { extension: PlainHighlight, surfaceClassName: "has-highlight" },
  textAlign: { extension: ClassTextAlign, surfaceClassName: "has-text-align" },
};

/** Deduplicated, in canonical order. */
export const resolveRichFormattingExtensions = (
  enabled: readonly RichFormattingExtension[]
): RichFormattingExtension[] =>
  RICH_FORMATTING_EXTENSIONS.filter((name) => enabled.includes(name));

export const getRichFormattingExtensions = (
  enabled: readonly RichFormattingExtension[]
): AnyExtension[] =>
  resolveRichFormattingExtensions(enabled).map(
    (name) => richFormattingRegistry[name].extension
  );

export const getRichFormattingClassNames = (
  enabled: readonly RichFormattingExtension[]
): string[] =>
  resolveRichFormattingExtensions(enabled).map(
    (name) => richFormattingRegistry[name].surfaceClassName
  );
